
import { httpClient, HttpMethod } from './http-client';
import {
  AddressAttributeResponseType,
  DispatchDestinationNodeType,
  FaultCode,
  LocationNodeData,
  NodeAttributesReponse,
  NodeData,
  PrioritySelectionObjectType,
  QueryOption,
  SiblingParentDetails,
  SiteSchemaData,
  SiteSchemaDropdown,
  StructureMergeDecisionData,
  StructureResponse,
  StructureType,
  StructureTypeResponse,
} from '../@types';
import { StructurePartListType } from '../pages/Structures/AddEditStructureLocation/PartsAccordianFragment';
import { StructureShipmentListType } from '../pages/Structures/AddEditStructureLocation/ShipmentAccordianFragment';

// Query options interface for paginated structure requests - extends base QueryOption
export interface StructureQueryOptions extends QueryOption {
  excludedColumns?: string;
}

// Response type for paginated structure lists
export interface StructureListResponse {
  previousPage?: number;
  currentPage?: number;
  nextPage?: number;
  total?: number;
  limit?: number;
  data?: StructureResponse[];
}

// Structure creation response
export interface StructureCreationResponse extends StructureResponse {
  parentNodeId: string;
  nodeTypeCode: string;
}

// Priority selection response
export interface PrioritySelectionResponse {
  priority: string;
  priorityName: string;
}

// Fleet tag response
export interface FleetTagItem {
  id: number;
  name: string;
}

// Multiple service creation access response
export interface MultipleServiceCreationAccess {
  hasAccess: boolean;
}

// Child location count response - API returns an object keyed by node type name with counts, plus a Total
export type ChildLocationCount = Record<string, number>;

export const getStructureTypesByCategoryCode = (catagoryCode: string): Promise<StructureTypeResponse[]> => {
  return httpClient<StructureTypeResponse[]>(`/nodeTypeCategories/${catagoryCode}/nodeTypes`, HttpMethod.Get);
};

export const createStructureType = (nodeType: StructureType) => {
  return httpClient('/nodeTypes', HttpMethod.Post, nodeType);
};

export const getStructuresByTypeCategoryCode = (catagoryCode: string, queryOptions: StructureQueryOptions): Promise<StructureListResponse> => {
  const { filterModel, page, pageSize, sortModel, excludedColumns } = queryOptions;
  let url = `/nodeTypeCategories/${catagoryCode}/nodes?page=${page + 1}&limit=${pageSize}&excludedColumns=${excludedColumns}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter) => {
    url += `&${filter?.field}=${filter?.operator},${filter?.value ?? ''}`;
  });

  return httpClient<StructureListResponse>(url, HttpMethod.Get);
};

export const getStructuresByTypeCategoryCodeAndTypeCode = (catagoryCode: string, typeCode: string): Promise<StructureResponse[]> => {
  return httpClient<StructureResponse[]>(`/nodeTypeCategories/${catagoryCode}/nodes/${typeCode}`, HttpMethod.Get);
};

export const getNodeChildrenByNodeType = (nodeId: string, nodeTypeCode: string): Promise<StructureResponse[]> => {
  return httpClient<StructureResponse[]>(`/nodes/${nodeId}/children?nodeTypeCode=${nodeTypeCode}`, HttpMethod.Get);
};

export const getNodeChildren = (nodeId: string, includeInactiveChildren = false): Promise<StructureResponse[]> => {
  return httpClient<StructureResponse[]>(`/nodes/${nodeId}/children${includeInactiveChildren ? '?includeInactiveChildren=true' : ''}`, HttpMethod.Get);
};

export const createStructure = (node: Record<string, unknown>): Promise<StructureCreationResponse> => {
  return httpClient<StructureCreationResponse>('/nodes', HttpMethod.Post, node);
};

export const getNodeFilteredAttributesByCustomRange = (nodeId: string, nodeAttributes: string, serviceTypeCode: string, faultType?: string, actionTypeCode?: string): Promise<NodeAttributesReponse[]> => {
  let url = `/nodes/${nodeId}/filteredAttributes?nodeAttributes=${nodeAttributes}&serviceTypeCode=${serviceTypeCode}`;
  if (faultType) {
    url += `&faultType=${faultType}`;
  }
  if (actionTypeCode) {
    url += `&actionTypeCode=${actionTypeCode}`;
  }

  return httpClient<NodeAttributesReponse[]>(url, HttpMethod.Get);
};

export const getNodeAttributesByCustomRange = (nodeId: string, nodeAttributes: string, visibilityBypassServiceId?: number): Promise<NodeAttributesReponse[]> => {
  let url = `/nodes/${nodeId}/attributes?nodeAttributes=${nodeAttributes}`;
  if (visibilityBypassServiceId) {
    url += `&visibilityBypassServiceId=${visibilityBypassServiceId}`;
  }
  return httpClient<NodeAttributesReponse[]>(url, HttpMethod.Get);
};

export const updateStructure = (nodeId: string, node: Record<string, unknown>): Promise<StructureCreationResponse> => {
  return httpClient<StructureCreationResponse>(`/nodes/${nodeId}`, HttpMethod.Put, node);
};

export const updateStructureBasicData = (nodeId: string, node: Record<string, unknown>): Promise<StructureResponse> => {
  return httpClient<StructureResponse>(`/nodes/${nodeId}/basic`, HttpMethod.Put, node);
};

export const getNodeById = (nodeId: string, nodeTypeCategoryCode?: string): Promise<StructureResponse> => {
  let url = `/nodes/${nodeId}`;
  if (nodeTypeCategoryCode) {
    url += `?nodeTypeCategoryCode=${nodeTypeCategoryCode}`;
  }
  return httpClient<StructureResponse>(url, HttpMethod.Get);
};

export const getNodeByName = (name: string, nodeTypeCode: string, parentNodeId?: number): Promise<StructureResponse> => {
  const parentNodeIdParam = parentNodeId ? `&parentNodeId=${parentNodeId}` : '';
  return httpClient<StructureResponse>(`/nodes/name/${name}/nodeTypeCode/${nodeTypeCode}?${parentNodeIdParam}`, HttpMethod.Get);
};

export const getNodeSubTree = (nodeId: string): Promise<StructureResponse[]> => {
  return httpClient<StructureResponse[]>(`/nodes/${nodeId}/subtree`, HttpMethod.Get);
};

export const getParentAttributesByPropertyType = (nodeId: string, propertyTypeCode: string, visibilityBypassServiceId?: number): Promise<NodeAttributesReponse[]> => {
  let url = `/nodes/${nodeId}/getparentattributes/${propertyTypeCode}`;
  if (visibilityBypassServiceId) {
    url += `?visibilityBypassServiceId=${visibilityBypassServiceId}`;
  }
  return httpClient<NodeAttributesReponse[]>(url, HttpMethod.Get);
};

export const getPrioritySelection = (prioritySelectionObject: PrioritySelectionObjectType): Promise<PrioritySelectionResponse> => {
  return httpClient<PrioritySelectionResponse>('/nodes/priorityselection', HttpMethod.Post, prioritySelectionObject);
};

export const getSuggestgedNodesFromAllocationEngine = (serviceTypeCode: string, actionTypeCode: string, processTypeCode: string, brandId?: number, mainLocationId?: number, serviceId?: number): Promise<LocationNodeData[]> => {
  return httpClient<LocationNodeData[]>(`/preferedNodesByAllocationEngine?serviceTypeCode=${serviceTypeCode}&actionTypeCode=${actionTypeCode}&processTypeCode=${processTypeCode}&brandId=${brandId}&mainLocationId=${mainLocationId}&serviceId=${serviceId}`, HttpMethod.Get);
};

export const getSiteSchemasByClientContract = (clientId: string, contractId: string): Promise<SiteSchemaDropdown[]> => {
  return httpClient<SiteSchemaDropdown[]>(`/nodeattributes/client/${clientId}/contract/${contractId}`, HttpMethod.Get);
};

export const getAllPartsForStructures = (limit: number, page: number, nodeId: number, isEnableToShowChildrenParts: boolean): Promise<StructurePartListType> => {
  const url =
    `/getPartsByStructureLocationId?limit=${limit}&page=${page + 1}&locationId=${nodeId}&isEnableToShowChildrenParts=${isEnableToShowChildrenParts}`;
  return httpClient<StructurePartListType>(url, HttpMethod.Get);
};

export const getAllSiteSchemas = (nodeId: number): Promise<SiteSchemaData[]> => {
  return httpClient<SiteSchemaData[]>(`/siteschema/nodeId/${nodeId}`, HttpMethod.Get);
};

export const getSiteSchema = (siteSchemaId: number): Promise<SiteSchemaData> => {
  return httpClient<SiteSchemaData>(`/siteschema/${siteSchemaId}`, HttpMethod.Get);
};

export const postSiteSchema = (data: SiteSchemaData): Promise<SiteSchemaData> => {
  return httpClient<SiteSchemaData>('/siteschema', HttpMethod.Post, data as unknown as Record<string, unknown>);
};

export const updateSiteSchema = (data: SiteSchemaData): Promise<SiteSchemaData> => {
  return httpClient<SiteSchemaData>(`/siteschema/${data.id}`, HttpMethod.Put, data as unknown as Record<string, unknown>);
};

export const getLoggedInSiblingParentDetails = (returnImmediateParentNode?: boolean): Promise<SiblingParentDetails> => {
  let url = '/nodes/loggedinsibling/parentdetails';
  if (returnImmediateParentNode) {
    url += `?returnImmediateParentNode=${returnImmediateParentNode}`;
  }
  return httpClient<SiblingParentDetails>(url, HttpMethod.Get);
};

export const getAllShipmentsForStructures = (limit: number, page: number, nodeId: number, isEnableToShowChildrenShipments: boolean): Promise<StructureShipmentListType> => {
  const url =
    `/getShipmentsByStructureLocationId?limit=${limit}&page=${page + 1}&locationId=${nodeId}&isEnableToShowChildrenShipments=${isEnableToShowChildrenShipments}`;
  return httpClient<StructureShipmentListType>(url, HttpMethod.Get);
};

export const getDestinationAddressByNodeId = (nodeId: string): Promise<AddressAttributeResponseType[]> => {
  return httpClient<AddressAttributeResponseType[]>(`/nodes/getLocationAttributes/${nodeId}`, HttpMethod.Get);
};


export const getNodeAttributesByPropertyTypeAndNodeIds = (propertyTypeCode: string, nodeIds: string): Promise<NodeAttributesReponse[]> => {
  const url = `/nodes/attributes/${propertyTypeCode}/getByIds?nodeIds=${nodeIds}`;
  return httpClient<NodeAttributesReponse[]>(url, HttpMethod.Get);
};

export const getNodeAttributesByNodeId = (nodeId: number): Promise<NodeAttributesReponse[]> => {
  const url = `/nodes/${nodeId}/attributes`;
  return httpClient<NodeAttributesReponse[]>(url, HttpMethod.Get);
};

export const getAllNodesByNodeType = (nodeTypeCode: string): Promise<NodeData[]> => {
  return httpClient<NodeData[]>(`/nodeTypes/${nodeTypeCode}/nodes`, HttpMethod.Get);
};

export const mergeStructure = (data: StructureMergeDecisionData): Promise<StructureResponse> => {
  return httpClient<StructureResponse>(`/locationMerge/currentNode/${data.currentNodeId}/mergeNode/${data.mergeNodeId}`, HttpMethod.Put, data as unknown as Record<string, unknown>);
};

export const getDispatchDestinationSuggestions = (serviceId: number, mainLocationNodeId: number, subLocationNodeId: number, primaryAllocationNodeId: number, secondaryAllocationNodeId: number): Promise<DispatchDestinationNodeType[]> => {
  const url = `/destinationSuggestions/dispatchShipment?serviceId=${serviceId}&mainLocationNodeId=${mainLocationNodeId}&subLocationNodeId=${subLocationNodeId}&primaryAllocationNodeId=${primaryAllocationNodeId}&secondaryAllocationNodeId=${secondaryAllocationNodeId}`;
  return httpClient<DispatchDestinationNodeType[]>(url, HttpMethod.Get);
};

export const getNodeAttributeById = (nodeAttributeId: number): Promise<FaultCode> => {
  return httpClient<FaultCode>(`/nodeattributes/${nodeAttributeId}`, HttpMethod.Get);
};

export const getAllocationNodeParentData = (nodeId: number): Promise<NodeData> => {
  return httpClient<NodeData>(`/allocationNodeParentData/${nodeId}`, HttpMethod.Get);
};

export const getCommonFleetTagsByPartTypeIds = (partTypeIds: number[]): Promise<FleetTagItem[]> => {
  return httpClient<FleetTagItem[]>('/getCommonFleetTagsByPartTypeIds', HttpMethod.Post, { partTypeIds });
};

export const checkMultipleServiceCreationAccess = (nodeId: string): Promise<MultipleServiceCreationAccess> => {
  const url = `/nodes/${nodeId}/checkMultiCreationRule`;
  return httpClient<MultipleServiceCreationAccess>(url, HttpMethod.Get);
};

export const childLocationCountGroupedByNodeType = (locationId: string): Promise<ChildLocationCount> => {
  let url = '/childLocationsOneLevelDown';

  if (locationId) {
    url += `?locationId=${locationId}`;
  }

  return httpClient<ChildLocationCount>(url, HttpMethod.Get);
};